/** The report's charts (item 71), drawn from the facts the report was written
 *  from - never from its words - in the Insights chart language (item 68:
 *  useChartColors, the chart theme, mirrored axes in a right-to-left report).
 *
 *  - Activity over time: conversations a day (or a week), one column each, the
 *    busiest marked by its height only; a column's messages and people on hover.
 *  - By organization: this period's conversations beside the period before,
 *    largest first, at most eight (the table below has them all).
 *
 *  A report reads in ITS language, so its charts take its direction and its
 *  numbers; the caption under each says what is shown. The plot itself is laid
 *  out left to right with its axes mirrored by axisDirection: an SVG label in a
 *  right-to-left box anchors from its other end and runs into the bars. */
import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useLanguage } from '@/hooks/useLanguage';
import { localeFor } from '@/utils/locale';
import { axisDirection, formatCount } from '../../utils/chartModel';
import { useChartTheme } from '../../utils/chartTheme';
import { useChartColors } from '../../utils/useChartColors';
import { utcDate } from './reportParts';

const ORG_BARS = 8;
const RADIUS_END: [number, number, number, number] = [0, 4, 4, 0];
const RADIUS_START: [number, number, number, number] = [4, 0, 0, 4];

interface Point { date: string; conversations: number; messages: number; active_users: number }
interface Compared { current: number | null; previous: number | null }
interface OrgRow { name: string; conversations: Compared }

export interface ChartFacts {
  series?: { unit: 'day' | 'week'; points: Point[] };
  organizations?: { rows: OrgRow[] };
}

interface Props {
  facts: ChartFacts | null;
  /** The report's own language: its words, numbers and direction. */
  language: 'en' | 'he';
}

function dayLabel(iso: string, language: Props['language']): string {
  return new Intl.DateTimeFormat(localeFor(language), { day: 'numeric', month: 'short', timeZone: 'UTC' })
    .format(utcDate(iso));
}

function Tip({ title, rows, rtl }: { title: string; rows: [string, string][]; rtl: boolean }) {
  return (
    <div className="tpl-tip" dir={rtl ? 'rtl' : 'ltr'}>
      <div className="tpl-tip__title">{title}</div>
      {rows.map(([name, value]) => (
        <div key={name} className="tpl-tip__row">
          <span className="tpl-tip__name">{name}</span>
          <bdi className="tpl-tip__num">{value}</bdi>
        </div>
      ))}
    </div>
  );
}

/** Conversations per day (or week) of the period. */
export function ReportTrendChart({ facts, language }: Props) {
  const { t } = useLanguage();
  const colors = useChartColors();
  const chrome = useChartTheme();
  const rtl = language === 'he';
  const lng = { lng: language };
  const locale = localeFor(language);
  const points = useMemo(() => (facts?.series?.points || []).map((p) => ({
    ...p, label: dayLabel(p.date, language),
  })), [facts, language]);
  if (points.length < 2) return null;
  const axis = axisDirection(rtl);
  const tick = { fontSize: 11, fill: chrome.axisText };
  const weekly = facts?.series?.unit === 'week';
  return (
    <figure className="rp-chart" dir={rtl ? 'rtl' : 'ltr'} data-testid="report-trend-chart">
      <div className="rp-chart__plot" dir="ltr" role="img"
           aria-label={t(weekly ? 'insights.reports.chart.trendAltWeeks' : 'insights.reports.chart.trendAltDays',
             { ...lng, n: points.length })}>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={points} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barCategoryGap="22%">
            <CartesianGrid stroke={chrome.gridLine} vertical={false} />
            <XAxis dataKey="label" reversed={axis.reversed} tick={tick} tickLine={false}
                   axisLine={{ stroke: chrome.xAxisLine }} minTickGap={12} interval="preserveStartEnd" />
            <YAxis orientation={axis.valueAxisSide} tick={tick} tickLine={false} axisLine={false} width={40}
                   allowDecimals={false} tickFormatter={(v: number) => formatCount(v, locale)} />
            <Tooltip
              cursor={{ fill: chrome.subtleBg }}
              content={({ active, payload }) => {
                const p = payload?.[0]?.payload as (Point & { label: string }) | undefined;
                if (!active || !p) return null;
                return (
                  <Tip rtl={rtl}
                       title={weekly ? t('insights.reports.chart.weekOf', { ...lng, date: p.label }) : p.label}
                       rows={[
                         [t('insights.reports.chart.conversations', lng), formatCount(p.conversations, locale)],
                         [t('insights.reports.chart.messages', lng), formatCount(p.messages, locale)],
                         [t('insights.reports.chart.people', lng), formatCount(p.active_users, locale)],
                       ]} />
                );
              }}
            />
            <Bar dataKey="conversations" fill={colors.lead} radius={[4, 4, 0, 0]} maxBarSize={44}
                 isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <figcaption className="rp-chart__caption">
        {t(weekly ? 'insights.reports.chart.trendCaptionWeeks' : 'insights.reports.chart.trendCaptionDays', lng)}
      </figcaption>
    </figure>
  );
}

/** This period's conversations by organization, beside the period before. */
export function ReportOrganizationsChart({ facts, language }: Props) {
  const { t } = useLanguage();
  const colors = useChartColors();
  const chrome = useChartTheme();
  const rtl = language === 'he';
  const lng = { lng: language };
  const locale = localeFor(language);
  const rows = useMemo(() => (facts?.organizations?.rows || [])
    .map((r) => ({ name: r.name, now: r.conversations?.current ?? 0, before: r.conversations?.previous ?? 0 }))
    .sort((a, b) => b.now - a.now || b.before - a.before)
    .slice(0, ORG_BARS), [facts]);
  if (rows.length < 2) return null;
  const axis = axisDirection(rtl);
  const radius = rtl ? RADIUS_START : RADIUS_END;
  const tick = { fontSize: 11, fill: chrome.axisText };
  const nameWidth = Math.min(180, Math.max(90, ...rows.map((r) => r.name.length * 7)));
  return (
    <figure className="rp-chart" dir={rtl ? 'rtl' : 'ltr'} data-testid="report-org-chart">
      <ul className="rp-chart__legend" aria-hidden="true">
        <li><span className="tpl-swatch" style={{ background: colors.lead }} />{t('insights.reports.chart.thisPeriod', lng)}</li>
        <li><span className="tpl-swatch" style={{ background: colors.other }} />{t('insights.reports.chart.before', lng)}</li>
      </ul>
      <div className="rp-chart__plot" dir="ltr" role="img" aria-label={t('insights.reports.chart.orgAlt', { ...lng, n: rows.length })}>
        <ResponsiveContainer width="100%" height={Math.max(150, rows.length * 52 + 30)}>
          <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 12, left: 12, bottom: 0 }}
                    barCategoryGap="28%" barGap={2}>
            <CartesianGrid stroke={chrome.gridLine} horizontal={false} />
            <XAxis type="number" reversed={axis.reversed} tick={tick} tickLine={false} axisLine={false} allowDecimals={false}
                   tickFormatter={(v: number) => formatCount(v, locale)} />
            <YAxis type="category" dataKey="name" orientation={axis.valueAxisSide} width={nameWidth} tick={tick}
                   tickLine={false} axisLine={{ stroke: chrome.xAxisLine }} />
            <Tooltip
              cursor={{ fill: chrome.subtleBg }}
              content={({ active, payload }) => {
                const r = payload?.[0]?.payload as { name: string; now: number; before: number } | undefined;
                if (!active || !r) return null;
                return (
                  <Tip rtl={rtl} title={r.name} rows={[
                    [t('insights.reports.chart.thisPeriod', lng), formatCount(r.now, locale)],
                    [t('insights.reports.chart.before', lng), formatCount(r.before, locale)],
                  ]} />
                );
              }}
            />
            <Bar dataKey="now" fill={colors.lead} radius={radius} maxBarSize={20}
                 isAnimationActive={false} />
            <Bar dataKey="before" fill={colors.other} radius={radius} maxBarSize={20}
                 isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <figcaption className="rp-chart__caption">{t('insights.reports.chart.orgCaption', lng)}</figcaption>
    </figure>
  );
}
